import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useEvent } from 'react-use-event-hook'
import { LOGIN_CALLBACK_URL } from 'config/config'
import { SNACKBAR_VARIANTS } from 'elements/Snackbar/Snackbar.consts'
import { usePhotoBooksStore } from 'stores/RootStoreProvider'
import type {
  BookArrival,
  ExternalBookSource,
} from 'stores/photoBooksStore/PhotoBooksStore'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import { getBookArrivalNoticeContent } from '../bookArrivalNoticeContent'
import { isPhotoBookEditorPath } from '../isPhotoBookEditorPath'
import {
  isLoginNoticePending,
  isLoginReloadPending,
  takeLoginNotice,
} from '../loginNotice'
import {
  appendPendingArrival,
  clearPendingArrival,
  readPendingArrival,
} from '../pendingArrivalStorage'

// The embedded login reloads the document, so the login flag is read on a fresh
// page while the account request is still in flight. Long enough for the
// account's books to land, short enough that a failed or unauthenticated fetch
// does not swallow the message.
const ACCOUNT_BOOKS_WAIT_MS = 4000

// Ordering again receives its books on the order page and navigates into the
// editor right after, so this notice waits for the editor it is headed to
// rather than for the page it was recorded on.
const EDITOR_BOUND_BOOK_SOURCE: ExternalBookSource = 'Reorder'

export function BookArrivalNoticeSnackbar() {
  const photoBooksStore = usePhotoBooksStore()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const location = useLocation()
  const [hasAccountBooksWaitElapsed, setHasAccountBooksWaitElapsed] =
    useState(false)
  // Seeded from storage, not from the store: the arrival that a reload or a
  // route change interrupted was recorded by a store instance that is gone.
  const [pendingArrival, setPendingArrival] = useState<BookArrival | null>(
    readPendingArrival
  )

  const { hasPendingArrival, isAnyEditorReady, hasMergedAccountBooks } =
    useMobxSelector(
      () => ({
        hasPendingArrival: photoBooksStore.pendingBookArrival !== null,
        isAnyEditorReady: photoBooksStore.isAnyEditorReady,
        hasMergedAccountBooks: photoBooksStore.hasMergedAccountBooks,
      }),
      [photoBooksStore]
    )

  // Drained the moment the merge records it, and only then decided about: the
  // store keeps the arrival in memory, so anything that ends the document
  // between the merge and the notice would otherwise lose it for good.
  useEffect(() => {
    if (!hasPendingArrival) return

    const arrival = photoBooksStore.takePendingBookArrival()
    if (!arrival) return
    setPendingArrival(appendPendingArrival(arrival))
  }, [hasPendingArrival, photoBooksStore])

  const showArrivalNotice = useEvent(() => {
    const content = getBookArrivalNoticeContent({
      arrival: pendingArrival,
      loginTitleKey: takeLoginNotice(),
    })
    if (!content) return

    clearPendingArrival()
    setPendingArrival(null)

    enqueueSnackbar('', {
      variant: SNACKBAR_VARIANTS.COMMON,
      name: content.name,
      title: t(content.titleKey, { count: content.bookCount }),
      analyticsProperties: content.analyticsProperties,
    })
  })

  useEffect(() => {
    if (!isLoginNoticePending()) return undefined

    const timeoutId = setTimeout(
      () => setHasAccountBooksWaitElapsed(true),
      ACCOUNT_BOOKS_WAIT_MS
    )
    return () => clearTimeout(timeoutId)
  }, [location])

  useEffect(() => {
    // Both are pages on their way somewhere else — the login callback redirects
    // as soon as it has an account, and a marked reload replaces the document.
    // Everything here keeps until the destination renders it.
    if (isLoginReloadPending()) return
    if (location.pathname === LOGIN_CALLBACK_URL) return

    const isLoginPending = isLoginNoticePending()
    if (!pendingArrival && !isLoginPending) return
    // A notice over the editor's splash would announce books the user cannot
    // see yet, and one fired on the way there would be gone by the time they
    // arrive.
    const isEditorBound =
      isPhotoBookEditorPath(location.pathname) ||
      pendingArrival?.source === EDITOR_BOUND_BOOK_SOURCE
    if (isEditorBound && !isAnyEditorReady) return
    if (
      isLoginPending &&
      !hasMergedAccountBooks &&
      !hasAccountBooksWaitElapsed
    ) {
      return
    }

    showArrivalNotice()
  }, [
    location,
    pendingArrival,
    isAnyEditorReady,
    hasMergedAccountBooks,
    hasAccountBooksWaitElapsed,
    showArrivalNotice,
  ])

  return null
}
